import { AlertCircle } from '@untitled-ui/icons-react';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../../../../../components/buttons/button';
import { Checkbox } from '../../../../../../components/checkbox-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { FeaturedIcon } from '../../../../../../components/featured-icon';
import { TextField } from '../../../../../../components/inputs/text-field';
import { CancelReason } from '../../../../../../entity/api/organization-billing';
import { tNamespace } from '../../../../../../services/translation';
import { formatDateToDdMmYyyy } from '../../../../../../shared/date';

const tBilling = tNamespace('app.billing.general');

export type CancelPlanModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessUntilDate: number;
  isTrialing?: boolean;
  onConfirmCancellation?: (reasons: CancelReason[], otherText?: string) => void;
  onKeepPlan?: () => void;
  loading?: boolean;
};

export function CancelPlanModal(props: CancelPlanModalProps) {
  const {
    open,
    onOpenChange,
    accessUntilDate,
    isTrialing,
    onConfirmCancellation,
    onKeepPlan,
    loading,
  } = props;

  const [selectedReason, setSelectedReason] = useState<CancelReason | null>(null);
  const [otherText, setOtherText] = useState('');

  function handleReasonChange(reason: CancelReason) {
    setSelectedReason(reason);
  }

  function handleKeepPlan() {
    if (onKeepPlan != null) {
      onKeepPlan();
    } else {
      onOpenChange(false);
    }
  }

  function handleConfirmCancellation() {
    if (onConfirmCancellation != null && selectedReason != null) {
      onConfirmCancellation(
        [selectedReason],
        selectedReason === CancelReason.Other ? otherText : undefined
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="w-[800px] max-w-full">
        <div className="flex flex-col gap-6">
          {/* Warning Icon */}
          <div className="flex justify-center">
            <FeaturedIcon icon={<AlertCircle />} size="md" color="red" border={true} />
          </div>

          {/* Title and Description */}
          <div className="flex flex-col gap-2">
            <DialogTitle align="center">{tBilling('cancelPlan')}</DialogTitle>
            <DialogDescription align="center" className={isTrialing === true ? 'text-red-500' : ''}>
              {isTrialing === true
                ? tBilling('cancelPlanDescriptionTrial')
                : tBilling('cancelPlanDescription', {
                    date: formatDateToDdMmYyyy(new Date(accessUntilDate)),
                  })}
            </DialogDescription>
          </div>

          {/* Divider */}
          <div className="h-px w-full bg-branding-gray-200" />

          {/* Feedback Section */}
          <div className="flex flex-col gap-4">
            <p className="font-body-2-r text-center text-dark-gray">
              {tBilling('cancelReasonPrompt')}
            </p>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                { key: CancelReason.TooComplex, label: tBilling('complicated') },
                { key: CancelReason.TooExpensive, label: tBilling('expensive') },
                { key: CancelReason.LowQuality, label: tBilling('outputBad') },
                { key: CancelReason.SwitchedService, label: tBilling('foundAnother') },
              ].map(function (option) {
                const isSelected = selectedReason === option.key;
                return (
                  <div
                    key={option.key}
                    className={twMerge(
                      'flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-all',
                      isSelected
                        ? 'border-branding-accent bg-branding-accent-50'
                        : 'border-branding-gray-200 hover:border-branding-gray-300'
                    )}
                    onClick={function () {
                      handleReasonChange(option.key);
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={function () {
                        handleReasonChange(option.key);
                      }}
                      className="pointer-events-none shrink-0"
                    />
                    <span className="font-body-2-m text-branding-black">{option.label}</span>
                  </div>
                );
              })}
              {/* Other option spans full width */}
              <div
                className={twMerge(
                  'col-span-1 flex cursor-pointer items-center rounded-lg border p-4 transition-all md:col-span-2',
                  selectedReason === CancelReason.Other
                    ? 'border-branding-accent bg-branding-accent-50'
                    : 'border-branding-gray-200 hover:border-branding-gray-300',
                  selectedReason === CancelReason.Other ? 'gap-2 py-2.5' : 'gap-3'
                )}
                onClick={function () {
                  handleReasonChange(CancelReason.Other);
                }}
              >
                <Checkbox
                  checked={selectedReason === CancelReason.Other}
                  onCheckedChange={function () {
                    handleReasonChange(CancelReason.Other);
                  }}
                  className="pointer-events-none shrink-0"
                />
                {selectedReason === CancelReason.Other ? (
                  <div className="flex flex-1 items-center gap-2">
                    <span className="font-body-2-m text-branding-black whitespace-nowrap">
                      {tBilling('other')}
                    </span>
                    <TextField
                      sizeField="xs"
                      placeholder={tBilling('otherPlaceholder')}
                      value={otherText}
                      onChange={function (e) {
                        setOtherText(e.target.value);
                      }}
                      onClick={function (e) {
                        e.stopPropagation();
                      }}
                      autoFocus
                    />
                  </div>
                ) : (
                  <span className="font-body-2-m text-branding-black">{tBilling('other')}</span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <DialogFooter className="mt-2 flex flex-col gap-3 md:flex-row">
            <Button
              size="xl"
              variant="outline"
              className="w-full md:flex-1"
              onClick={handleKeepPlan}
              disabled={loading}
            >
              {tBilling('keepPlan')}
            </Button>
            <Button
              size="xl"
              variant="destructive"
              className="w-full md:flex-1"
              onClick={handleConfirmCancellation}
              loading={loading}
            >
              {tBilling('confirmCancellation')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
